import {Component, Mixins, Prop, Watch} from 'vue-property-decorator';
import Modal from "../../../components/Modal.vue";
import CheckboxControl from "../checkbox-control/checkboxControl.vue";
import {HTTP} from '@/services/HttpServices';
import {
    BusinessResidence,
    Service, ServiceAddress,
    ServiceType,
    ServiceTypeId
} from '@/products-and-services/ProductsAndServicesModels';
import LoadingMixin from '@/mixins/LoadingMixin';
import ThemeMixin from "@/mixins/ThemeMixin";
import BusinessResidenceMixin from "@/mixins/BusinessResidenceMixin";

@Component({
    name: "BekChannelGuide",
    components: { Modal, CheckboxControl },
    filters: {
        price(value: string) {
            if (!value) {
                return '';
            }
            return value.substring(0, value.indexOf('.'));
        }
    }
})

export default class BekChannelGuide extends Mixins(LoadingMixin, ThemeMixin, BusinessResidenceMixin) {
    @Prop({type: [Boolean], required: false, default: false})
    showChannelGuide!: boolean;
    @Prop({type: [Boolean], required: false, default: false})
    setBusinessModelSize!: string;

    protected MIN_MAX_WIDTH = 667;
    private search: string = "";
    private showModal: Boolean = this.showChannelGuide || false;
    private tvPackages: TvPackages[] = [];
    private categories: Category[] = [];
    private maxWidth: string = this.MIN_MAX_WIDTH.toString(10);
    private filterCategoriesEnabled = false;
    private channelInfo: ChannelsRequest = { premiumGroups: [], packages: [], categories: [], pricing: [] };
    private channelGuideMinWidth = 667;


    get publicCategories() {
        return this.categories.filter( category => category.displayToPublic);
    }

    get VisibleChannels() {
        const mapLambda = (p: TvPackages)  => ({
            ...p,
            groups: p.groups.map(g => ({
                ...g,
                showGroupName: g.channels.some(c => c.available),
                channels: g.channels.filter(c => this.channelShouldBeShown(c) && p.isSelected)
            }))
        });
        const filteredPackages = this.tvPackages.filter(pkg => pkg.isSelected);
        const packages = filteredPackages.map(mapLambda);

        return packages;
    }

    get showPricing(): boolean {
        return Boolean(!!this.location);
    }

    get getEastWestRegionToFilter(): LocalEastWestChannels | undefined {
        const customersRegion = Number.parseInt(this.$store.state.shoppingCartData.regionId, 10);
        if (customersRegion) {
            const westRegion = [1, 2, 4];
            return westRegion.includes(customersRegion) ?
                LocalEastWestChannels.EAST_LOCAL :
                LocalEastWestChannels.WEST_LOCAL;
        }
        return;
    }

    get TVService() {
        return this.$store.state.shoppingCartData.availableOptions?.serviceTypes?.filter((serviceType: ServiceType) => serviceType.id === ServiceTypeId.TV).pop();
    }

    get location(): ServiceAddress | undefined {
        return this.$store.getters.getLocation;
    }

    @Watch('location')
    public async locationChanged() {
        await this.getPackages();
    }

    getPackagePrice(packageName: string) {
        if (!this.showPricing) {
            return '';
        }

        let packagePrice = '';
        const tvService: ServiceType = this.TVService;
        if (tvService && tvService.services) {
            const tvServiceForPackage: Service | undefined = tvService.services
                .find((service: Service) => service.name && service.name.toLowerCase().includes(packageName.toLowerCase()));
            if (tvServiceForPackage && tvServiceForPackage.amount) {
                packagePrice = tvServiceForPackage.amount.split('.')[0] || '';
            }
        }
        return packagePrice;
    }

    packageSelectionChange(pkg: TvPackages) {
        const packageCount = this.tvPackages.filter(pkg => pkg.isSelected).length;
        if (packageCount > 2) {
            pkg.isSelected = !pkg.isSelected;
        } else {
            if (!pkg.isSelected) {
                pkg.isSelected = !pkg.isSelected;
            }
        }
    }

    channelShouldBeShown(c: TvPackageChannel) {
        // Hide if search filters out
        if (this.search) {
            if (c.name.toLowerCase().indexOf(this.search.toLowerCase()) < 0) {
                return false;
            }
        }

        // Hide if category button filters out
        if (!this.filterCategoriesEnabled) {
            return true;
        }
        const category = c.categories;
        if (category) {
            if (this.categories
                .filter(cat => cat.isSelected && category.includes(cat.id)).length > 0
            ) {
                return true;
            }
        }
        return false;
    }

    async created() {
        await this.getPackages();
        this.calculateWidth();
        window.onresize = () => this.calculateWidth();
    }

    private calculateWidth() {
        if (this.setBusinessModelSize) {
            this.maxWidth = '550';
            this.channelGuideMinWidth = 550;
            document.documentElement.style.setProperty('--channel-guide-min-width', '');
            document.documentElement.style.setProperty('--channel-guide-package-columns', '3');
            return;
        }
        document.documentElement.style.setProperty('--channel-guide-min-width', '667px');
        document.documentElement.style.setProperty('--channel-guide-package-columns', '6');
        if (window.innerWidth > 1022) {
            this.maxWidth = '1022';
        } else {
            this.maxWidth = (window.innerWidth * .97).toString(10);
        }
    }

    /*
     * Only ran on created and when location changes to keep the modal responsive.
     */
    async getPackages() {
        await this.execute(async () => {
            this.channelInfo = await this.getChannels();
            this.categories = this.channelInfo.categories.map(c => ({
                ...c,
                displayToPublic: true,
                isSelected: false // Add isSelected for filtering purposes
            }));

            this.setTvPackages();

        }, 'Failed to retrieve channel data.');
    }

    setTvPackages() {
        const regionToFilter = this.getEastWestRegionToFilter;
        function getGroupPrice(tvPackage: TvPackages, group: ChannelGroups, channelRequest: ChannelsRequest): string {
            if (group.name && tvPackage.name && group.name.toUpperCase() === 'SPORTS' && tvPackage.name.toUpperCase().startsWith('PRIME')) {
                if (tvPackage.name.toUpperCase() ==='PRIME+') {
                    const sports = channelRequest.pricing.find(price => price.name === 'PRIME+ SPORTS');
                    if (sports) {
                        return sports.price;
                    }
                } else {
                    const sports = channelRequest.pricing.find(price => price.name === 'PRIME SPORTS');
                    if (sports) {
                        return sports.price;
                    }
                }
            }
            return '';
        }

        this.tvPackages = this.channelInfo.packages
            // Add isSelected for filtering purposes
            .map(p => {
                return {
                    ...p,
                    isSelected: true,
                    groups: p.groups.filter(g => g.name !== regionToFilter).map(g => ({
                        id: g.id,
                        name: g.name,
                        type: g.type,
                        displayOrder: g.displayOrder,
                        price: (getGroupPrice(p, g, this.channelInfo)) ? getGroupPrice(p, g, this.channelInfo) : g.price,
                        isSelected: false,
                        channels: g.channels.map(c => {
                            return {
                                ...c,
                                isSelected: false,
                            };
                        })
                    }))
                };
            });
    }

    updateOtherChannels(packageId: string, channelId: string) {
        // When a channel in 1 package is selected, update the matching channel in the other packages.
        // Packages & Groups are small arrays.
        this.tvPackages
            .forEach(p => p.groups
                .forEach(g => g.channels
                    .forEach(c => {
                        c.isSelected = c.id === channelId ? !c.isSelected : c.isSelected
                    })
                )
            );
    }

    unselectAllChannels(channelId: string) {
        // Every channel that matches an id needs to be false, regardless of package.
        this.tvPackages
            .forEach(p => p.groups
                .forEach(g => g.channels
                    .forEach(c => {
                        if (c.id === channelId) {
                            c.isSelected = false;
                        }
                    })
                )
            );
    }

    filterCategories() {
        this.filterCategoriesEnabled = this.categories.filter(c => c.isSelected).length > 0;
    }

    openModal() {
        this.setTvPackages();
        this.showModal = true;
    }

    closeModal() {
        this.showModal = false;
    }

    getChannels(): Promise<ChannelsRequest> {
        const businessResidence = this.shouldShowResidentialItems ? BusinessResidence.Residence : BusinessResidence.Business;
        let locationQuery = ''
        if (this.location) {
            locationQuery = `&location=${this.location.addressId}`;
        }
        return HTTP.get(`/public/digital-tv-packages?businessResidence=${businessResidence}${locationQuery}`) as Promise<ChannelsRequest>;
    }

    isPremiumPackage(type: GroupingType): boolean {
        return type === GroupingType.PREMIUM;
    }

    updateCosts(group: ChannelGroups) {
        let addGroupCost = false;
        this.tvPackages
            .forEach(pack => pack.groups
                .forEach(loopGroup => {
                    if (group.id ==loopGroup.id) {
                        loopGroup.isSelected = !loopGroup.isSelected;
                        addGroupCost = loopGroup.isSelected;
                    }
                })
            );

        this.tvPackages.forEach(p => {
            if (p.price) {
                if (addGroupCost) {
                    p.price = (parseInt(p.price, 10 ) + parseInt(group.price, 10)).toString(10);
                } else {
                    p.price = (parseInt(p.price, 10 ) - parseInt(group.price, 10)).toString(10);
                }
            }
        });
    }
}
interface GroupPackageCost {
    packageName: PackageNames;
    packageCost: number;
}

enum PackageNames {
    Select,
    Choice,
    ChoicePlus,
    Prime,
    PrimePlus
}

interface Category {
    id: string;
    name: string;
    isSelected: boolean;
    displayToPublic: boolean;
}

interface ChannelsRequest {
    premiumGroups: ChannelGroups[];
    packages: TvPackages[];
    categories: ChannelCategory[];
    pricing: EPICPricing[];
}

interface EPICPricing {
    name: string;
    price: string;
}

interface TvPackages {
    id: string;
    name: string;
    isSelected: boolean;
    price?: string;
    groups: ChannelGroups[]
}

interface ChannelGroups {
    id: string,
    name: string,
    type: GroupingType,
    displayOrder: number,
    price: string,
    isSelected: boolean;
    channels: TvPackageChannel[]
}

interface ChannelCategory {
    id: string;
    name: string;
}

interface TvPackageChannel {
    name: string,
    id: string,
    channelNumber: string,
    hdChannelNumber: string,
    type: GroupingType,
    group: string,
    packages: string[],
    categories: string[],
    available: boolean,
    isSelected: boolean;
}

enum GroupingType {
    PACKAGE = 'Package',
    PREMIUM = 'Premium'
}

enum GroupNameExcusions {
    SPORTS = 'Sports'
}

enum LocalEastWestChannels {
    EAST_LOCAL = 'East Locals',
    WEST_LOCAL = 'West Locals'
}
